<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="clients-page-title">
      <router-link
              to="/administration/guide/create"
              class="btn btn-success float-right"
              qid="clients-create-button"
      >
        {{ $t('administration.guide.create_button') }}
      </router-link>
      {{ $t('administration.guide.title') }}
    </h4>

    <b-tabs class="mt-3">
      <!-- English tab -->
      <b-tab :title="$t('administration.guide.language.en')" active @click="englishTabClicked">
        <div class="card mt-4">
          <table class="table table-responsive-md">
            <thead>
            <tr>
              <th scope="col">{{ $t('administration.guide.create.name_label') }}</th>
              <th scope="col">{{ $t('administration.guide.create.content_label') }}</th>
              <th scope="col">{{ $t('administration.guide.create.type_label') }}</th>
              <th scope="col">{{ $t('administration.guide.create.external_id_label') }}</th>
              <th scope="col">{{ $t('system.enabled') }}</th>
              <th scope="col">{{ $t('administration.guide.create.updated_by') }}</th>
              <th width="150"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="object in tableItems">
              <td>
                <router-link
                    :to="{ name: 'administration.guideView', params: { slug: object.slug }}"
                    qid="guide-name"
                >
                  {{object.title}}
                </router-link>
              </td>
              <td>
                {{ $t('administration.guide.content.' + $options.filters.slugify(object.type)) }}
              </td>
              <td>{{getScopeLabel(object.scope, object.type)}}</td>
              <td>{{object.external_id}}</td>
              <td>
                <div v-if="object.enabled">
                  <div class="fs-1-5 float-left"
                       @click="setEnabled(object, 0)"
                  >
                    <font-awesome-icon class="text-success cursor-pointer" icon="toggle-on" />
                  </div>
                </div>
                <div v-if="!object.enabled">
                  <div class="fs-1-5 float-left"
                       @click="setEnabled(object, 1)"
                  >
                    <font-awesome-icon class="text-muted cursor-pointer" icon="toggle-off" />
                  </div>
                </div>
              </td>
              <td>{{object.updated_at | formatDate}}</td>
              <td>
                <span class="text-muted mr-3 cursor-pointer" qid="clients-edit-button" @click="showEditClientModal(object)">
                  <font-awesome-icon icon="pen"/>
                </span>
                <span class="text-muted cursor-pointer" qid="clients-delete-button" @click="showConfirmDeleteModal(object)">
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </b-tab>

      <!-- Danish language tab -->
      <b-tab :title="$t('administration.guide.language.da')" @click="danishTabClicked">
        <div class="card mt-4">
          <table class="table table-responsive-md">
            <thead>
            <tr>
              <th scope="col">{{ $t('administration.guide.create.name_label') }}</th>
              <th scope="col">{{ $t('administration.guide.create.content_label') }}</th>
              <th scope="col">{{ $t('administration.guide.create.type_label') }}</th>
              <th scope="col">{{ $t('administration.guide.create.updated_by') }}</th>
              <th width="150"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="object in tableItems">
              <td>
                <router-link
                    :to="{ name: 'administration.guideView', params: { slug: object.slug }}"
                    qid="guide-name"
                >
                  {{object.title}}
                </router-link>
              </td>
              <td>
                {{ $t('administration.guide.content.' + $options.filters.slugify(object.type)) }}
              </td>
              <td>{{getScopeLabel(object.scope, object.type)}}</td>
              <td>{{object.updated_at | formatDate}}</td>
              <td>
                <span class="text-muted mr-3 cursor-pointer" qid="clients-edit-button" @click="showEditClientModal(object)">
                  <font-awesome-icon icon="pen"/>
                </span>
                <span class="text-muted cursor-pointer" qid="clients-delete-button" @click="showConfirmDeleteModal(object)">
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </b-tab>

    </b-tabs>


    <!-- Delete client confirm modal template -->
    <b-modal ref="delete-client" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-client-title">
          {{ $t('administration.guide.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('administration.guide.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('administration.guide.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-client-cancel-button"
        >
          {{ $t('administration.guide.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteClient(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-client-submit-button"
        >
          {{ $t('administration.guide.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit client modal template -->
    <b-modal ref="edit-client" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-client-title">
          {{ $t('administration.guide.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-c-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editClientFormSubmit" qid="edit-client-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-guide-content-label">
              {{ $t('administration.guide.create.language') }}
            </label>
            <multiselect
                v-model="selectedLanguage"
                :options="languages"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('administration.guide.create.language')"
                label="name"
                track-by="name"
                qid="create-s-form-content"
            ></multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="create-guide-name-label">
              {{ $t('administration.guide.create.name_label') }}
            </label>
            <b-form-input
                qid="create-guide-form-name"
                type="text"
                v-model="guideForm.title"
                required
                :placeholder="$t('administration.guide.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-guide-description-label">
              {{ $t('administration.guide.create.description_label') }}
            </label>
            <wysiwyg v-model="guideForm.description" />
          </b-form-group>
          <b-form-group>
            <label qid="create-guide-content-label">
              {{ $t('administration.guide.create.content_label') }}
            </label>
            <multiselect
                v-model="selectedContentType"
                :options="contentTypes"
                :placeholder="$t('administration.guide.create.content_placeholder')"
                label="name"
                @select="selectedType = []"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                track-by="name"
                qid="create-s-form-content"
            ></multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="create-guide-type-label">
              {{ $t('administration.guide.create.type_label') }}
            </label>
            <multiselect
                v-model="selectedType"
                :options="getTypes(selectedContentType)"
                :placeholder="$t('administration.guide.create.type_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                track-by="name"
                qid="create-s-form-type"
            ></multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="create-guide-name-label">
              {{ $t('administration.guide.create.external_id_label') }}
            </label>
            <b-form-input
                qid="create-guide-form-name"
                type="text"
                v-model="guideForm.external_id"
                required
                :placeholder="$t('administration.guide.create.external_id_placeholder')"
            ></b-form-input>
          </b-form-group>

          <b-form-group>
            <b-form-checkbox v-model="guideForm.enabled" name="check-button" switch class="icon-success">
              {{ $t('administration.guide.create.enabled_label') }}
            </b-form-checkbox>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditClientModal"
                    qid="edit-client-cancel-button"
          >
            {{ $t('administration.clients.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="edit-client-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('administration.clients.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import permissionCheck from '@/util/ACL/permissionCheck.js'

  export default {
    name: 'Guide',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('administration.title'),
            to: '/administration'
          },
          {
            text: this.$t('administration.guide.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        guideForm: {
          slug: '',
          enabled: false,
          external_id: '',
          title: '',
          description: '',
          language: '',
        },
        error: '',
        loading: true,
        buttonDisabled: false,
        selectedContentType: [],
        selectedType: [],
        language: 'en',
        languages: [
          { name: 'English', value: 'en' },
          { name: 'Danish', value: 'da' },
        ],
        currentTab: 'en',
        selectedLanguage: [],
        contentTypes: [
          { name: 'Activity', value: 'Activity' },
          { name: 'Process', value: 'Process' },
          { name: 'System', value: 'System' },
          { name: 'Contract', value: 'Third party' },
          { name: 'Third party', value: 'Vendor' },
          { name: 'Incident category', value: 'Incident' },
          { name: 'Policy', value: 'Policy' },
          { name: 'Risk scenario', value: 'Risk scenario' },
          { name: 'Generic control', value: 'Generic control' },
          { name: 'Project', value: 'Project' },
          { name: 'Task', value: 'Task' },
          { name: 'Settings', value: 'Maintenance' },
        ],
        activityTypes: [
          { name: 'List page', value: 'activity_list_page' },
          { name: 'Update basic info', value: 'update_basic_info' },
          { name: 'Update description', value: 'update_description' },
          { name: 'Update policies and procedures', value: 'update_policies_and_procedures' },
          { name: 'Update departments', value: 'update_organization' },
          { name: 'Update systems', value: 'update_systems' },
          { name: 'Update third party sources', value: 'update_third_party_sources' },
          { name: 'Update third party receivers', value: 'update_third_party_receivers' },
          { name: 'Data transfer', value: 'data_mapping' },
          { name: 'Copy dataflow from other source', value: 'copy_dataflow' },
          { name: 'Update processing role', value: 'update_processing_role' },
          { name: 'Update data subject', value: 'update_data_subject' },
          { name: 'Update retention rule', value: 'update_retention_rule' },
          { name: 'Update integrity check', value: 'update_integrity_check' },
          { name: 'Update data mapping description', value: 'update_data_mapping_description' },
          { name: 'Update processing type', value: 'update_processing_type' },
          { name: 'Update measures', value: 'update_measures' },
          { name: 'Update risk scenarios', value: 'update_risk_scenarios' },
          { name: 'Validate retention rule', value: 'validate_retention_rule' },
          { name: 'Validate integrity check', value: 'validate_integrity_check' },
          { name: 'Validate data mapping description', value: 'validate_mapping_description' },
          { name: 'Validate processing type', value: 'validate_processing_type' },
          { name: 'Validate risk scenarios', value: 'validate_risk_scenarios' },
        ],
        systemTypes: [
          { name: 'List page', value: 'system_list_page' },
          { name: 'Update basic info', value: 'update_basic_info' },
          { name: 'Update description', value: 'update_description' },
          { name: 'Update third parties', value: 'update_third_parties' },
          { name: 'Update policies and procedures', value: 'update_policies_and_procedures' },
          { name: 'Update data objects', value: 'update_data_objects' },
          { name: 'Update measures', value: 'update_measures' },
          { name: 'Update activities', value: 'update_activities' },
          { name: 'Update assessments', value: 'update_assessments' },
          { name: 'Update RPO', value: 'assessment_rpo' },
          { name: 'Update RTO', value: 'assessment_rto' },
          { name: 'Update system classification', value: 'system_classification' },
          { name: 'View system assessment', value: 'system_assessment' },
          { name: 'Update risk scenarios', value: 'update_risk_scenarios' },
          { name: 'Validate risk scenarios', value: 'validate_risk_scenarios' },
        ],
        processTypes: [
          { name: 'List page', value: 'process_list_page' },
          { name: 'Update basic info', value: 'update_basic_info' },
          { name: 'Update description', value: 'update_description' },
          { name: 'Update purpose', value: 'update_purpose' },
          { name: 'Update policies and procedures', value: 'update_policies_and_procedures' },
          { name: 'Update legal basis', value: 'update_legal_basis' },
          { name: 'Update information obligation', value: 'update_information_obligation' },
          { name: 'Update risk profile', value: 'update_risk_profile' },
          { name: 'Update loss of confidentiality', value: 'update_loss_of_confidentiality' },
          { name: 'Update loss of integrity', value: 'update_loss_of_integrity' },
          { name: 'Update loss of availability', value: 'update_loss_of_availability' },
          { name: 'Update risk scenarios', value: 'update_risk_scenarios' },
          { name: 'Validate legal basis', value: 'validate_legal_basis' },
          { name: 'Validate information obligation', value: 'validate_information_obligation' },
          { name: 'Validate risk profile', value: 'validate_risk_profile' },
          { name: 'Validate loss of confidentiality', value: 'validate_confidentiality_impact_and_probability' },
          { name: 'Validate loss of integrity', value: 'validate_integrity_impact_and_probability' },
          { name: 'Validate loss of availability', value: 'validate_availability_impact_and_probability' },
          { name: 'Validate risk scenarios', value: 'validate_risk_scenarios' },
        ],
        thirdPartyTypes: [
          { name: 'List page', value: 'third_party_list_page' },
          { name: 'Update basic info', value: 'update_basic_info' },
          { name: 'Update description', value: 'update_description' },
          { name: 'Update data', value: 'update_data' },
          { name: 'Update data processor agreement', value: 'update_data_processor_agreement' },
          { name: 'Update activities', value: 'update_activities' },
          { name: 'Update systems', value: 'update_systems' },
          { name: 'Update measures', value: 'update_measures' },
          { name: 'Update documents', value: 'update_documents' },
          { name: 'Update contact persons', value: 'update_contacts' },
          { name: 'Update sub-data processors', value: 'update_subdata_processors' },
          { name: 'Update risk scenarios', value: 'update_risk_scenarios' },
          { name: 'Validate third party', value: 'validate_third_party' },
          { name: 'Validate risk scenarios', value: 'validate_risk_scenarios' },
        ],
        vendorTypes: [
          { name: 'List page', value: 'vendor_list_page' },
          { name: 'Update basic info', value: 'update_basic_info' },
          { name: 'Update description', value: 'update_description' }
        ],
        incidentTypes: [
          { name: 'List page', value: 'incident_category_list_page' },
          { name: 'Update basic info', value: 'update_basic_info' },
          { name: 'Create incident category', value: 'create_incident_category' },
          { name: 'Update description', value: 'update_description' },
          { name: 'Update settings', value: 'update_settings' },
          { name: 'Update risk scenario', value: 'update_risk_scenarios' },
          { name: 'Update systems', value: 'update_systems' },
          { name: 'Update responsibles', value: 'update_responsibles' },
          { name: 'Update escalation point', value: 'update_escalation_point' },
          { name: 'Update schedule', value: 'update_schedule' },
          { name: 'Update questions', value: 'update_questions' },
          { name: 'Update tasks', value: 'update_tasks' }
        ],
        riskScenarioTypes: [
          { name: 'List page', value: 'risk_list_page' },
          { name: 'Update basic info', value: 'risk_update_basic_info' },
          { name: 'Update description', value: 'risk_update_description' },
          { name: 'Update vulnerabilities', value: 'risk_update_vulnerability' },
          { name: 'Update threats', value: 'risk_update_threat' },
          { name: 'Update activity business risks', value: 'risk_update_activities_business_risks' },
          { name: 'Update process business risks', value: 'risk_update_processes_business_risks' },
          { name: 'Update system business risks', value: 'risk_update_systems_business_risks' },
          { name: 'Update third party business risks', value: 'risk_update_third_parties_business_risks' },
          { name: 'Update assessments', value: 'risk_update_assessments' },
          { name: 'Update validation', value: 'risk_update_validation' },
        ],
        genericControlsTypes: [
          { name: 'List page', value: 'generic_controls_list_page' },
          { name: 'Update basic info', value: 'generic_controls_update_basic_info' },
          { name: 'Update description', value: 'generic_controls_update_description' },
          { name: 'Update vulnerabilities', value: 'generic_controls_update_vulnerability' },
          { name: 'Update threats', value: 'generic_controls_update_threat' },
          { name: 'Update risk scenarios', value: 'generic_controls_update_risk_scenarios' },
          { name: 'Update policies and procedures', value: 'generic_controls_update_policies' },
          { name: 'Update measures', value: 'generic_controls_update_measures' },
          { name: 'Update projects', value: 'generic_controls_update_projects' },
          { name: 'Update framework controls', value: 'generic_controls_update_framework_controls' },
          { name: 'Update validation', value: 'generic_controls_update_validation' },
        ],
        projectTypes: [
          { name: 'List page', value: 'projects' },
          { name: 'Asset projects page', value: 'asset_projects' },
          { name: 'Control projects page', value: 'control_projects' },
          { name: 'Assessment projects page', value: 'assessment_projects' },
          { name: 'Gantt page', value: 'gantt_page' },
          { name: 'Update generic control scope', value: 'project_generic_controls' },
          { name: 'My to-do list page', value: 'my_todo_list_page' },
          { name: 'Rejected validations list page', value: 'rejected_validations_list_page' },
        ],
        policyTypes: [
          { name: 'List page', value: 'policies_and_procedures_list' },
          { name: 'Update basic info', value: 'update_basic_info' },
          { name: 'Update description', value: 'update_description' },
          { name: 'Update department', value: 'update_department' },
          { name: 'Update relations', value: 'update_relations' },
          { name: 'Update documents', value: 'update_documents' },
        ],
        taskTypes: [
          { name: 'Update task', value: 'update_task' },
          { name: 'Validation task', value: 'validation_task' },
          { name: 'Control/Security measure task', value: 'control_task' },
          { name: 'System description task', value: 'system_description_task' },
          { name: 'Documentation collection task', value: 'data_collection_task' },
          { name: 'Assessment task', value: 'assessment_task' }
        ],
        maintenanceTypes: [
          { name: 'Partitions list', value: 'partitions_list' },
          { name: 'Partition', value: 'partition' },
          { name: 'Users and owners list', value: 'users_and_owners_list' },
          { name: 'Data objects list', value: 'data_objects_list' },
          { name: 'Data object', value: 'data_object' },
          { name: 'Process groups list', value: 'process_groups_list' },
          { name: 'Departments list', value: 'organizations_list' },
          { name: 'Department', value: 'organization' },
          { name: 'Create measure', value: 'create_measure' },
          { name: 'Legal basis list', value: 'legal_basis_list' },
          { name: 'Legal basis', value: 'legal_basis' },
          { name: 'Data subjects list', value: 'data_subjects_list' },
          { name: 'Data subject', value: 'data_subject' },
          { name: 'Information obligation list', value: 'information_obligation_list' },
          { name: 'Information obligation', value: 'information_obligation' },
          { name: 'Retention rules list', value: 'retention_rules_list' },
          { name: 'Retention rule', value: 'retention_rule' },
          { name: 'Threats list', value: 'threats_list' },
          { name: 'Threat', value: 'threat' },
          { name: 'Vulnerabilities list', value: 'vulnerabilities_list' },
          { name: 'Vulnerability', value: 'vulnerability' },
          { name: 'Frameworks list', value: 'frameworks_list' },
          { name: 'Framework', value: 'framework' },
          { name: 'Risk categories list', value: 'risk_categories_list' },
          { name: 'Risk category', value: 'risk_category' },
          { name: 'Assessment templates list', value: 'assessment_templates_list' },
          { name: 'Assessment template', value: 'assessment_template' },
          { name: 'Assessment template description', value: 'assessment_template_description' },
          { name: 'Assessment template measures', value: 'assessment_template_measures' },
          { name: 'Assessment template questions', value: 'assessment_template_questions' },
          { name: 'Update assessment template', value: 'update_assessment_template' },
          { name: 'Upload report template', value: 'report_template' },
        ]
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " of " + selectedOf
      }
    },
    methods: {
      loadGuides(page, perPage, language = 'en') {
        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        piincHttp.get('guides', { params: {
            page: pageNumber,
            per_page: perPageNumber,
            language: 'en'
          }}).then(function(response) {
          self.tableItems = response.data.items;
          self.loading = false;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {

        });
      },
      englishTabClicked() {
        this.currentTab = 'en'
        this.loadGuides('', '', this.currentTab)
      },
      danishTabClicked() {
        this.currentTab = 'da'
        this.loadGuides('', '', this.currentTab)
      },
      getTypes(selectedType) {
        if (selectedType && selectedType.value) {
          if (selectedType.value == 'Activity') {
            return this.activityTypes
          }
          if (selectedType.value == 'System') {
            return this.systemTypes
          }
          if (selectedType.value == 'Process') {
            return this.processTypes
          }
          if (selectedType.value == 'Third party') {
            return this.thirdPartyTypes
          }
          if (selectedType.value == 'Vendor') {
            return this.vendorTypes
          }
          if (selectedType.value == 'Incident') {
            return this.incidentTypes
          }
          if (selectedType.value == 'Risk scenario') {
            return this.riskScenarioTypes
          }
          if (selectedType.value == 'Generic control') {
            return this.genericControlsTypes
          }
          if (selectedType.value == 'Project') {
            return this.projectTypes
          }
          if (selectedType.value == 'Task') {
            return this.taskTypes
          }
          if (selectedType.value == 'Maintenance') {
            return this.maintenanceTypes
          }
          if (selectedType.value == 'Policy') {
            return this.policyTypes
          }
        } else {
          return []
        }
      },
      pageChanged(page) {
        this.loadGuides(page, '', this.currentTab)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadGuides(1, perPage.value, this.currentTab)
        self.selectedPerPage = perPage.value
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadGuides(1, self.selectedPerPage)
      },
      isGranted(permission) {
        return permissionCheck(permission)
      },
      confirmDeleteClient(guide) {
        let self = this;
        piincHttp.delete('guides/' + guide.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadGuides(self.currentPage, '', self.currentTab);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('administration.guide.delete.success'))
        }, function() {

        });
      },
      setEnabled(object, type) {
        let self = this;
        piincHttp.patch('guides/' + object.slug + '/enabled', {enabled: type}).then(function(response) {
          self.loadGuides(self.currentPage);
        }, function(error) {})
      },
      editClientFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        this.guideForm.type = this.selectedContentType.value ? this.selectedContentType.value : '';
        this.guideForm.scope = this.selectedType.value ? this.selectedType.value : '';

        // Additional check - if dpa is selected, append it to form data
        if (this.selectedLanguage && this.selectedLanguage.value) {
          this.guideForm.language = this.selectedLanguage.value;
        }

        if (this.selectedLanguage && this.selectedLanguage.value) {
          piincHttp.put('guides/' + this.guideForm.slug, this.guideForm).then(function(response) {
            self.hideEditClientModal();
            self.loadGuides(self.currentPage, '', self.currentTab);
            self.$toastr('success', self.$t('administration.guide.update.success'))
            self.buttonDisabled = false;
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        } else {
          this.error = this.$t('administration.guide.create.language_error')
          this.buttonDisabled = false;
        }
      },
      showEditClientModal(object) {
        this.guideForm.slug = object.slug
        this.guideForm.title = object.title
        this.guideForm.external_id = object.external_id
        if (object.enabled) {
          this.guideForm.enabled = true
        }
        this.guideForm.description = object.description

        this.selectedContentType = []
        if (object.type) {
          this.selectedContentType = _.find(this.contentTypes, function(item) {
            return item.value === object.type;
          });
        }

        this.selectedLanguage = []
        if (object.language) {
          this.selectedLanguage = _.find(this.languages, function(item) {
            return item.value === object.language;
          });
        }

        this.selectedType = []
        if (object.scope) {
          if (object.type == 'Activity') {
            this.selectedType = _.find(this.activityTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Process') {
            this.selectedType = _.find(this.processTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'System') {
            this.selectedType = _.find(this.systemTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Third party') {
            this.selectedType = _.find(this.thirdPartyTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Vendor') {
            this.selectedType = _.find(this.vendorTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Incident') {
            this.selectedType = _.find(this.incidentTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Risk scenario') {
            this.selectedType = _.find(this.riskScenarioTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Generic control') {
            this.selectedType = _.find(this.genericControlsTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Project') {
            this.selectedType = _.find(this.projectTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Task') {
            this.selectedType = _.find(this.taskTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Maintenance') {
            this.selectedType = _.find(this.maintenanceTypes, function(item) {
              return item.value === object.scope;
            });
          }
          if (object.type == 'Policy') {
            this.selectedType = _.find(this.policyTypes, function(item) {
              return item.value === object.scope;
            });
          }
        }
        this.$refs['edit-client'].show()
      },
      hideEditClientModal() {
        this.error = ''
        this.$refs['edit-client'].hide()
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-client'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {};
        this.$refs['delete-client'].hide()
      },
      getScopeLabel(code, type) {
        let matchedItem = {}
        if (type == 'Activity') {
          matchedItem = _.find(this.activityTypes, function(o) { return o.value == code; });
        }
        if (type == 'Process') {
          matchedItem =_.find(this.processTypes, function(o) { return o.value == code; });
        }
        if (type == 'System') {
          matchedItem =_.find(this.systemTypes, function(o) { return o.value == code; });
        }
        if (type == 'Third party') {
          matchedItem =_.find(this.thirdPartyTypes, function(o) { return o.value == code; });
        }
        if (type == 'Vendor') {
          matchedItem =_.find(this.vendorTypes, function(o) { return o.value == code; });
        }
        if (type == 'Incident') {
          matchedItem =_.find(this.incidentTypes, function(o) { return o.value == code; });
        }
        if (type == 'Risk scenario') {
          matchedItem =_.find(this.riskScenarioTypes, function(o) { return o.value == code; });
        }
        if (type == 'Generic control') {
          matchedItem =_.find(this.genericControlsTypes, function(o) { return o.value == code; });
        }
        if (type == 'Project') {
          matchedItem =_.find(this.projectTypes, function(o) { return o.value == code; });
        }
        if (type == 'Task') {
          matchedItem =_.find(this.taskTypes, function(o) { return o.value == code; });
        }
        if (type == 'Maintenance') {
          matchedItem =_.find(this.maintenanceTypes, function(o) { return o.value == code; });
        }
        if (type == 'Policy') {
          matchedItem =_.find(this.policyTypes, function(o) { return o.value == code; });
        }
        if(matchedItem && matchedItem.name){
          return matchedItem.name
        }else{
          return 'Missing scope '+code+' for type ' + type
        }
      }
    },
    components: {

    },
    created () {
      this.loadGuides();
    }
  }
</script>
